import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { ATTACHMENT_KIND_LABELS, type AssetDetail } from '@prism/contracts';
import { ApiError, errorMessage } from '../api/client';
import { useAttachments, useMe } from '../api/hooks';
import { toast } from '../components/Toasts';
import { bytes, relative } from '../lib/format';
import { queryKeys } from '../api/queryKeys';

/** Collateral that travels with the asset, stored in the workspace's collateral tier. */
export function AttachmentsTab({ asset }: { asset: AssetDetail }) {
  const me = useMe().data!;
  const q = useAttachments(asset.id);
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const canEdit = me.permissions.includes('asset.edit_metadata') && !asset.deleted;
  const items = q.data?.items ?? [];

  async function upload(files: FileList | null) {
    for (const file of Array.from(files ?? [])) {
      setUploading(file.name);
      try {
        const res = await fetch(`/api/v1/assets/${asset.id}/attachments?filename=${encodeURIComponent(file.name)}`, {
          method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/octet-stream' }, body: file,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new ApiError(res.status, body?.error?.code ?? 'UPLOAD_FAILED', body?.error?.message ?? `Upload failed (${res.status})`);
        }
        toast(`Attached ${file.name}`);
      } catch (err) {
        toast(errorMessage(err), 'error');
      }
    }
    setUploading(null);
    if (input.current) input.current.value = '';
    await qc.invalidateQueries({ queryKey: queryKeys.attachments(asset.id) });
  }

  async function remove(id: string, filename: string) {
    if (!window.confirm(`Remove ${filename}? The file is deleted from collateral storage.`)) return;
    const res = await fetch(`/api/v1/assets/${asset.id}/attachments/${id}`, { method: 'DELETE', credentials: 'same-origin' });
    if (!res.ok) toast((await res.json().catch(() => null))?.error?.message ?? 'Could not remove the attachment', 'error');
    else toast(`Removed ${filename}`);
    await qc.invalidateQueries({ queryKey: queryKeys.attachments(asset.id) });
  }

  return (
    <div data-testid="attachments-tab">
      <p className="muted" style={{ marginTop: 0 }}>Subtitles files, graphics, manuscripts and other documents that belong with this asset. They are kept in the collateral storage tier.</p>
      {canEdit && (
        <div style={{ marginBottom: 14 }}>
          <input ref={input} type="file" multiple hidden onChange={(e) => void upload(e.target.files)} data-testid="attachment-input" />
          <button className="btn sm" onClick={() => input.current?.click()} disabled={Boolean(uploading)}>
            {uploading ? <><Loader2 size={13} className="spin" /> Uploading {uploading}…</> : <><Upload size={13} /> Add files</>}
          </button>
        </div>
      )}
      {q.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : items.length === 0 ? (
        <p className="faint"><Paperclip size={13} style={{ verticalAlign: -2 }} /> No attachments yet.</p>
      ) : (
        <div className="steps">
          {items.map((a) => (
            <div className="step" key={a.id} data-testid="attachment-row">
              <span className="step-icon succeeded"><FileText size={12} /></span>
              <div className="step-main">
                <span className="step-title">{a.filename}</span>
                <span className="step-sub">{ATTACHMENT_KIND_LABELS[a.kind]} · {bytes(a.sizeBytes)} · {a.storageName}{a.source === 'watch_folder' ? ' · from a watch folder' : ''}{a.createdBy ? ` · ${a.createdBy}` : ''} · {relative(a.createdAt)}</span>
              </div>
              <a className="btn ghost sm icon" href={a.url} download={a.filename} aria-label={`Download ${a.filename}`}><Download size={13} /></a>
              {canEdit && <button className="btn ghost sm icon danger" aria-label={`Remove ${a.filename}`} onClick={() => void remove(a.id, a.filename)}><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
